'use client';
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

// Inline SVGs, not an icon font — Font Awesome's icon glyphs were failing to
// render here on both mobile and desktop (empty circles), so these are drawn
// directly instead of depending on a webfont load succeeding. Facebook/
// LinkedIn/X paths match the ones already used in src/components/v2/Footer.jsx
// for visual consistency across the site.
function ShareIcon({ name }) {
  const common = { viewBox: '0 0 24 24', width: 16, height: 16, 'aria-hidden': true };
  if (name === 'facebook') return <svg {...common} fill="currentColor"><path d="M13.5 21v-8h2.7l.4-3.1h-3.1v-2c0-.9.3-1.5 1.6-1.5h1.7V3.7c-.3 0-1.3-.1-2.4-.1-2.4 0-4.1 1.5-4.1 4.2v2.1H7.5v3.1h2.8v8h3.2Z" /></svg>;
  if (name === 'twitter') return <svg {...common} fill="currentColor"><path d="M18.9 3h2.9l-6.4 7.3 7.5 10.7H17l-4.6-6.5L6.7 21H3.8l6.8-7.8L3.4 3h5.9l4.2 6 5.4-6Zm-1 16.2h1.6L8.4 4.7H6.7l11.2 14.5Z" /></svg>;
  if (name === 'linkedin') return <svg {...common} fill="currentColor"><path d="M5.1 8.2A1.85 1.85 0 1 0 5.1 4.5a1.85 1.85 0 0 0 0 3.7ZM3.5 9.6h3.2V20H3.5V9.6Zm5.2 0h3.1V11c.5-.9 1.7-1.8 3.6-1.8 3.8 0 4.5 2.5 4.5 5.8v5h-3.2v-4.4c0-1.1 0-3-1.9-3s-2.2 1.2-2.2 2.9V20H9V9.6Z" /></svg>;
  if (name === 'whatsapp') return <svg {...common} fill="currentColor"><path d="M12 2.4A9.6 9.6 0 0 0 3.6 17l-1.1 4.6 4.7-1.1A9.6 9.6 0 1 0 12 2.4Zm0 1.9a7.7 7.7 0 0 1 6.1 12.4 7.7 7.7 0 0 1-11 2l-.4-.2-2.7.6.6-2.6-.3-.4A7.7 7.7 0 0 1 12 4.3Zm-3 3.6c-.2 0-.5 0-.7.3-.2.3-.9.9-.9 2.1s.9 2.4 1 2.6c.2.2 1.8 2.8 4.4 3.8 2.2.9 2.6.7 3.1.6.5 0 1.6-.6 1.8-1.3.2-.6.2-1.2.2-1.3-.1-.1-.3-.2-.6-.4-.3-.1-1.6-.8-1.9-.9-.2-.1-.4-.1-.6.1-.2.3-.7.9-.9 1.1-.1.2-.3.2-.6.1-.3-.2-1.2-.5-2.3-1.5-.8-.7-1.4-1.7-1.6-2-.1-.3 0-.4.1-.6l.4-.5c.1-.1.2-.3.2-.4.1-.2 0-.4 0-.5L9.5 8c-.2-.4-.4-.4-.5-.4H9Z" /></svg>;
  if (name === 'telegram') return <svg {...common} fill="currentColor"><path d="m3 11.4 16.7-6.4c.8-.3 1.5.2 1.2 1.4l-2.8 13.4c-.2.9-.8 1.1-1.5.7l-4.2-3.1-2 2c-.2.2-.4.4-.9.4l.3-4.5 8.2-7.4c.4-.3-.1-.5-.5-.2l-10.1 6.4-4.4-1.4c-.9-.3-.9-.9.2-1.3Z" /></svg>;
  if (name === 'email') return <svg {...common} fill="none" stroke="currentColor" strokeWidth="1.8"><rect x="3" y="5" width="18" height="14" rx="2.2" /><path d="m4 6.5 8 6 8-6" strokeLinecap="round" strokeLinejoin="round" /></svg>;
  if (name === 'link') return <svg {...common} fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><path d="M9.5 14.5 14.5 9.5" /><path d="M11 6.5 12.6 4.9a3.6 3.6 0 0 1 5.1 5.1L16 11.6" /><path d="M13 17.5 11.4 19.1a3.6 3.6 0 0 1-5.1-5.1L8 12.4" /></svg>;
  return <svg {...common} fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M12 3v12" /><path d="m8 7 4-4 4 4" /><path d="M5 13v6a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-6" /></svg>;
}

export default function TagsAndShare({
  tags = [],
  title = "",
  url,
  twitterVia = "yourbrand",
  hashtags = [],
  showEmptyTags = true,
}) {
  const [copied, setCopied] = useState(false);
  const { t } = useTranslation('call_to_action');
  const label = (key, fallback) => t(`labels.${key}`, fallback);

  const share = useMemo(() => {
    const href =
      url ||
      (typeof window !== "undefined"
        ? window.location.href
        : "https://www.adcast.app");
    const enc = encodeURIComponent;
    const encUrl = enc(href);
    const encTitle = enc(title || "");
    const encTags = enc(hashtags.join(","));

    return {
      href,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encUrl}`,
      twitter: `https://twitter.com/intent/tweet?url=${encUrl}&text=${encTitle}&via=${twitterVia}&hashtags=${encTags}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encUrl}`,
      whatsapp: `https://api.whatsapp.com/send?text=${encTitle}%20${encUrl}`,
      telegram: `https://t.me/share/url?url=${encUrl}&text=${encTitle}`,
      email: `mailto:?subject=${encTitle}&body=${encTitle}%0A${encUrl}`,
    };
  }, [title, url, twitterVia, hashtags]);

  const handleNativeShare = async (e) => {
    e.preventDefault();
    if (navigator.share) {
      try {
        await navigator.share({ title, url: share.href, text: title });
      } catch {
        /* user canceled */
      }
    }
  };

  const handleCopy = async (e) => {
    e.preventDefault();
    try {
      await navigator.clipboard.writeText(share.href);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard blocked */
    }
  };

  const shouldShowTags = showEmptyTags || tags.length > 0;

  return (
    <div className="row">
      <div className="col-sm-6 align-self-center">
        <div className="tags">
          {shouldShowTags && (
            <ul>
              <li className="meta_title">Tags:</li>
              {tags.length === 0 ? (
                <li><span>-</span></li>
              ) : (
                tags.map((tag) => (
                  <li key={tag}>
                    <a href="#">{tag}</a>
                  </li>
                ))
              )}
            </ul>
          )}
        </div>
      </div>

      <div className="col-sm-6 align-self-center">
        <div className="share_social">
          <ul>
            <li className="meta_title">{label('share', 'Share:')}</li>
            <li className="d-inline-block d-sm-none">
              <a href={share.href} onClick={handleNativeShare} title={label('native_share', 'Share')}>
                <ShareIcon name="native" />
              </a>
            </li>
            <li>
              <a href={share.facebook} target="_blank" rel="noopener noreferrer" aria-label={label('share_on_facebook', 'Share on Facebook')} title="Facebook">
                <ShareIcon name="facebook" />
              </a>
            </li>
            <li>
              <a href={share.twitter} target="_blank" rel="noopener noreferrer" aria-label={label('share_on_x', 'Share on X/Twitter')} title="Twitter">
                <ShareIcon name="twitter" />
              </a>
            </li>
            <li>
              <a href={share.linkedin} target="_blank" rel="noopener noreferrer" aria-label={label('share_on_linkedin', 'Share on LinkedIn')} title="LinkedIn">
                <ShareIcon name="linkedin" />
              </a>
            </li>
            <li className="d-none d-sm-inline-block">
              <a href={share.whatsapp} target="_blank" rel="noopener noreferrer" aria-label={label('share_on_whatsapp', 'Share on WhatsApp')} title="WhatsApp">
                <ShareIcon name="whatsapp" />
              </a>
            </li>
            <li className="d-none d-sm-inline-block">
              <a href={share.telegram} target="_blank" rel="noopener noreferrer" aria-label={label('share_on_telegram', 'Share on Telegram')} title="Telegram">
                <ShareIcon name="telegram" />
              </a>
            </li>
            <li>
              <a href={share.email} aria-label={label('share_via_email', 'Share via Email')} title="Email">
                <ShareIcon name="email" />
              </a>
            </li>
            <li>
              <a href={share.href} onClick={handleCopy} aria-label={label('copy_link', 'Copy link')} title={copied ? label('copied', 'Copied!') : label('copy_link', 'Copy link')}>
                <ShareIcon name="link" />
              </a>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
}
